import React, { useEffect, useRef } from "react";
import { animate, motion, useMotionValue } from "framer-motion";
import { IDEVRA, STATUS_META } from "@/data/idevra";
import { sfx } from "@/lib/sound";
import { buzz } from "@/lib/haptics";
import { useCoarsePointer } from "@/lib/useCoarsePointer";

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const rise = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
};
const SPRING = { type: "spring", stiffness: 300, damping: 30 };
const TILE_W = 150;
const STEP = TILE_W + 16;

const TileInner = ({ v, i }) => (
  <>
    <img src={v.cover} alt={v.name} loading="lazy" draggable={false} />
    <span className="iv-tile-shade" />
    <span className="iv-tile-gloss" />
    <span className="iv-tile-holo" aria-hidden="true" />
    <span className="absolute left-2.5 top-2.5 f-orbitron text-[10px] font-bold text-white/70">
      {String(i + 1).padStart(2, "0")}
    </span>
    <span className="absolute inset-x-0 bottom-0 flex flex-col gap-1 p-3">
      <span className="f-orbitron text-[11px] sm:text-xs font-bold leading-tight text-white">{v.name}</span>
      <span className={`iv-status ${STATUS_META[v.status].cls}`}>{STATUS_META[v.status].label}</span>
    </span>
  </>
);

function VentureShelf({ focusIdx, setFocusIdx, onOpen }) {
  const n = IDEVRA.ventures.length;
  const x = useMotionValue(-focusIdx * STEP);
  const roll = useRef({ active: false, idx: focusIdx, timer: null });

  useEffect(() => {
    const ctrl = animate(x, -focusIdx * STEP, SPRING);
    return () => ctrl.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusIdx]);

  useEffect(
    () =>
      x.on("change", (v) => {
        const r = roll.current;
        if (!r.active) return;
        const idx = Math.min(n - 1, Math.max(0, Math.round(-v / STEP)));
        if (idx !== r.idx) {
          r.idx = idx;
          sfx.tick();
          buzz();
        }
      }),
    [x, n]
  );

  const onStart = () => {
    const r = roll.current;
    clearTimeout(r.timer);
    r.active = true;
    r.idx = focusIdx;
  };

  const onEnd = (_e, info) => {
    const projected = x.get() + info.velocity.x * 0.18;
    const next = Math.min(n - 1, Math.max(0, Math.round(-projected / STEP)));
    const r = roll.current;
    r.timer = setTimeout(() => {
      r.active = false;
    }, 700);
    if (next !== focusIdx) setFocusIdx(next);
    else animate(x, -next * STEP, SPRING);
  };

  return (
    <div className="overflow-hidden py-2" style={{ perspective: 1200 }} data-testid="ventures-shelf">
      <motion.div
        className="flex gap-4"
        style={{ x }}
        drag="x"
        dragConstraints={{ left: -(n - 1) * STEP, right: 0 }}
        dragElastic={0.12}
        dragMomentum={false}
        onDragStart={onStart}
        onDragEnd={onEnd}
      >
        {IDEVRA.ventures.map((v, i) => (
          <motion.button
            key={v.slug}
            type="button"
            data-testid={`venture-tile-${v.slug}`}
            className={`iv-tile shrink-0 text-left ${i === focusIdx ? "focused" : ""}`}
            style={{ width: TILE_W }}
            animate={{ scale: i === focusIdx ? 1 : 0.9, opacity: i === focusIdx ? 1 : 0.72 }}
            transition={SPRING}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              setFocusIdx(i);
              onOpen(v);
            }}
          >
            <TileInner v={v} i={i} />
          </motion.button>
        ))}
      </motion.div>
      <p className="f-mono mt-4 text-[9px] tracking-[0.22em] text-[#94A3B8]">
        ◄ ► SWIPE THE SHELF · TAP A TITLE TO OPEN
      </p>
    </div>
  );
}

export default function VenturesPanel({ focusIdx, setFocusIdx, onOpen }) {
  const isTouch = useCoarsePointer();
  return (
    <motion.div variants={stagger} initial="hidden" animate="show" className="flex flex-col gap-6">
      <motion.div variants={rise} className="flex items-baseline justify-between">
        <span className="iv-eyebrow">— VENTURES · SELECT A TITLE</span>
        <span className="f-mono text-[9px] tracking-[0.25em] text-[#94A3B8]">
          {String(focusIdx + 1).padStart(2, "0")} / {String(IDEVRA.ventures.length).padStart(2, "0")}
        </span>
      </motion.div>

      {isTouch ? (
        <motion.div variants={rise}>
          <VentureShelf focusIdx={focusIdx} setFocusIdx={setFocusIdx} onOpen={onOpen} />
        </motion.div>
      ) : (
        <>
          <div
            className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-7"
            style={{ perspective: 1200 }}
            data-testid="ventures-grid"
          >
            {IDEVRA.ventures.map((v, i) => (
              <motion.button
                key={v.slug}
                type="button"
                variants={rise}
                data-testid={`venture-tile-${v.slug}`}
                className={`iv-tile text-left ${i === focusIdx ? "focused" : ""}`}
                onMouseEnter={() => {
                  if (i !== focusIdx) {
                    sfx.tick();
                    setFocusIdx(i);
                  }
                }}
                onFocus={() => setFocusIdx(i)}
                onClick={() => onOpen(v)}
                whileHover={{ rotateY: 6, rotateX: -3, scale: 1.05, z: 20 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: "spring", stiffness: 260, damping: 20 }}
              >
                <TileInner v={v} i={i} />
              </motion.button>
            ))}
          </div>
          <motion.p variants={rise} className="f-mono text-[10px] tracking-[0.22em] text-[#94A3B8]">
            ✕ SELECT A TILE TO OPEN THE VENTURE DOSSIER
          </motion.p>
        </>
      )}
    </motion.div>
  );
}
